"use client";

import React, { useState, useEffect } from "react";
import OrderService, { OrderListItem, ORDER_STATUS_LABELS, ORDER_STATUS, getNextStatusTransition } from "../services/order-service";
import ClothingCategoryService, { ClothingCategory } from "../../clothing-categories/services/clothing-category-service";
import EquipmentService, { Equipment, EQUIPMENT_TYPE, EQUIPMENT_TYPE_NAME } from "../../equipment/services/equipment-service";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";

const FALLBACK_CATEGORIES = [
  "Đồ trắng",
  "Đồ lao động",
  "Mỏng nhẹ",
  "Đồ cần tẩy",
  "Khác",
];

interface OrderChangeStatusModalProps {
  open?: boolean;
  order: OrderListItem | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (
    orderId: number,
    nextStatus: number,
    classificationData?: { items: Array<{ category: string; quantity: number }>; total_items: number; note?: string },
    extra?: { final_weight?: number; item_count?: number },
  ) => void;
  // Gọi sau khi gắn thiết bị (sọt/máy giặt/túi) thành công — hành động này KHÔNG đi qua onConfirm vì
  // không phải lời gọi đổi trạng thái (đóng túi tự chuyển trạng thái ở backend qua EQUIPMENT_AUTO_TRANSITION).
  onEquipmentDone: (message: string) => void;
  // Tải lại danh sách mà KHÔNG đóng modal — dùng sau "Đóng túi": modal ở lại, chuyển thẳng sang bước "Sẵn sàng giao".
  onRefresh?: () => void;
}

// Loại thiết bị dùng để theo dõi sản xuất — sọt HOẶC máy giặt (xem assertProductionTypeLock ở backend,
// chọn 1 trong 2 cho cả đơn, không trộn).
const PRODUCTION_EQUIPMENT_TYPES = [EQUIPMENT_TYPE.BASKET, EQUIPMENT_TYPE.WASHING_MACHINE];

const OrderChangeStatusModal: React.FC<OrderChangeStatusModalProps> = ({
  order,
  loading,
  onClose,
  onConfirm,
  onEquipmentDone,
  onRefresh,
}) => {
  // === State cho form phân loại (khi INSPECTING) ===
  const [categories, setCategories] = useState<string[]>(FALLBACK_CATEGORIES);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [classNote, setClassNote] = useState("");
  const [prefillLabel, setPrefillLabel] = useState<string | null>(null);
  // Giống app (modal-classification): CÓ số liệu điền sẵn → ẩn loại đồ không có số lượng cho gọn, thêm lại qua ô
  // "Thêm loại đồ" (tìm trong danh mục). Không có điền sẵn (khách mới) → hiện đủ danh mục.
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());

  // Bước cuối xưởng ("Hoàn thành sản xuất"): số ký (final_weight) + số lượng (item_count)
  const [readyWeight, setReadyWeight] = useState("");
  const [readyQty, setReadyQty] = useState("");

  // Chọn thiết bị (INSPECTING: sọt/máy giặt để "Hoàn thành kiểm tra"; WASHING: túi để "Đóng túi")
  const [productionEquipList, setProductionEquipList] = useState<Equipment[]>([]);
  const [selectedProductionEquipId, setSelectedProductionEquipId] = useState<number | null>(null);
  const [bagEquipList, setBagEquipList] = useState<Equipment[]>([]);
  const [selectedBagId, setSelectedBagId] = useState<number | null>(null);
  const [equipListLoading, setEquipListLoading] = useState(false);
  const [equipAssigning, setEquipAssigning] = useState(false);
  const [equipError, setEquipError] = useState<string | null>(null);
  // Vừa "Đóng túi" xong trong lần mở modal này — `order` (dòng danh sách) vẫn là bản cũ chưa có túi.
  const [bagJustAttached, setBagJustAttached] = useState(false);

  const { fetch: fetchCategories } = ClothingCategoryService.list();
  const { fetch: fetchLastClassification } = OrderService.lastClassification();
  const { fetch: fetchClassificationFromServices } = OrderService.classificationFromServices();
  // "Lấy số lượng từ dịch vụ": ghi đè đúng các loại đồ có liên kết dịch vụ (cái/lần), loại khác giữ nguyên.
  const [fillingFromServices, setFillingFromServices] = useState(false);
  const [fillMessage, setFillMessage] = useState<{ type: "success" | "warning"; text: string } | null>(null);
  const { fetch: saveClassification } = OrderService.saveClassification();
  const { fetch: fetchAvailableEquip } = EquipmentService.available();
  const { fetch: assignEquip } = EquipmentService.assign();

  const isInspecting = order ? Number(order.status) === ORDER_STATUS.INSPECTING : false;
  const isWashing = order ? Number(order.status) === ORDER_STATUS.WASHING : false;
  const orderBranchId = order ? Number(order.current_branch_id) || Number(order.branch_id) : 0;
  const hasFinalWeight = order ? Number(order.final_weight) > 0 : false;
  const hasBagAttached = order ? Number(order.active_bag_count) > 0 || bagJustAttached : false;

  // Fetch categories + prefill từ đơn gần nhất khi mở modal ở bước INSPECTING
  useEffect(() => {
    if (!order || !isInspecting) return;
    let mounted = true;
    const load = async () => {
      setLoadingCategories(true);
      let officialCategories = FALLBACK_CATEGORIES;
      try {
        const res = await fetchCategories({ status: 1 });
        if (res?.status === 200) {
          const data = res.data;
          const list: ClothingCategory[] = Array.isArray(data) ? data : (data as any)?.list ?? [];
          if (list.length > 0) officialCategories = list.map(c => c.name);
        }
      } catch {
        // giữ FALLBACK_CATEGORIES
      }
      if (!mounted) return;

      // Prefill số lượng từ đơn gần nhất của khách (nếu chi nhánh có bật tính năng này cho đơn).
      let prefillMap: Record<string, number> = {};
      let sourceLabel: string | null = null;
      if (Number(order.prefill_count_from_last) === 1) {
        try {
          const last = await fetchLastClassification(order.id);
          const items = last?.data?.items || [];
          if (items.length > 0) {
            prefillMap = Object.fromEntries(items.map(i => [i.category, Number(i.quantity) || 0]));
            sourceLabel = last?.data?.source_order_code ? `Đã điền từ đơn gần nhất ${last.data.source_order_code}` : "Đã điền từ đơn gần nhất";
          }
        } catch {
          // bỏ qua, để trống
        }
      }
      if (!mounted) return;

      // Union danh mục chính thức + danh mục có trong prefill (phòng khi đơn trước dùng loại đồ đã ẩn)
      const allCats = [...officialCategories];
      for (const c of Object.keys(prefillMap)) if (!allCats.includes(c)) allCats.push(c);

      // Sắp xếp: loại đồ CÓ dữ liệu prefill (>0) lên đầu để dễ thao tác; giữ nguyên thứ tự tương đối
      // trong từng nhóm (stable partition).
      const withData = allCats.filter(c => (prefillMap[c] || 0) > 0);
      const withoutData = allCats.filter(c => !((prefillMap[c] || 0) > 0));
      const sortedCats = [...withData, ...withoutData];

      setCategories(sortedCats);
      setQuantities(Object.fromEntries(sortedCats.map(c => [c, prefillMap[c] > 0 ? String(prefillMap[c]) : ""])));
      setHiddenCategories(withData.length > 0 ? new Set(withoutData) : new Set());
      setPrefillLabel(sourceLabel);
      setLoadingCategories(false);
    };
    load();
    return () => { mounted = false; };
  }, [order, isInspecting]);

  // Fetch thiết bị khả dụng: INSPECTING → sọt/máy giặt để chọn; WASHING → túi để "Đóng túi"
  useEffect(() => {
    if (!order || !orderBranchId || (!isInspecting && !isWashing)) return;
    let mounted = true;
    const load = async () => {
      setEquipListLoading(true);
      try {
        if (isInspecting) {
          const results = await Promise.all(
            PRODUCTION_EQUIPMENT_TYPES.map(type => fetchAvailableEquip({ branch_id: orderBranchId, type })),
          );
          if (!mounted) return;
          const merged: Equipment[] = results.flatMap(r => {
            const data = r?.data as any;
            return Array.isArray(data) ? data : data?.list ?? [];
          });
          setProductionEquipList(merged);
        } else if (isWashing && hasFinalWeight && !hasBagAttached) {
          const res = await fetchAvailableEquip({ branch_id: orderBranchId, type: EQUIPMENT_TYPE.BAG });
          if (!mounted) return;
          const data = res?.data as any;
          setBagEquipList(Array.isArray(data) ? data : data?.list ?? []);
        }
      } catch {
        if (mounted) {
          setProductionEquipList([]);
          setBagEquipList([]);
        }
      } finally {
        if (mounted) setEquipListLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [order, isInspecting, isWashing, orderBranchId, hasFinalWeight, hasBagAttached]);

  // Reset form khi đóng/mở
  useEffect(() => {
    setBagJustAttached(false);
    if (!order) {
      setQuantities({});
      setHiddenCategories(new Set());
      setClassNote("");
      setReadyWeight("");
      setReadyQty("");
      setPrefillLabel(null);
      setFillMessage(null);
      setSelectedProductionEquipId(null);
      setSelectedBagId(null);
      setProductionEquipList([]);
      setBagEquipList([]);
    } else {
      // Prefill số ký/số lượng từ đơn (nếu đã có) cho bước "Hoàn thành sản xuất"
      const fw = Number(order.final_weight);
      const ic = Number(order.item_count);
      setReadyWeight(fw > 0 ? String(fw) : "");
      setReadyQty(ic > 0 ? String(ic) : "");
    }
  }, [order]);

  if (!order) return null;

  const currentStatus = Number(order.status);

  const handleAssignBag = async () => {
    if (!selectedBagId) return;
    setEquipAssigning(true);
    setEquipError(null);
    try {
      // Quét/gắn túi giờ CHỈ GẮN, không tự chuyển "Sẵn sàng giao" nữa — tách riêng để chi nhánh (thường
      // là nhà máy) kịp tạo phiếu điều chuyển trả về gốc trước khi thật sự sang bước giao.
      // skip_actor_assignment: web không tự gán người bấm vào vai của bước — theo bảng phân công.
      const res = await assignEquip(selectedBagId, { order_id: Number(order.id), skip_actor_assignment: true });
      if (res?.status === 200) {
        // Ở lại modal, chuyển thẳng sang bước "Sẵn sàng giao" (trước đây đóng modal → người dùng phải bấm lại).
        // Vẫn giữ quyền "Trở lại" để điều chuyển trước khi giao. Tải lại danh sách ngầm để dòng có túi mới.
        setBagJustAttached(true);
        setSelectedBagId(null);
        onRefresh?.();
      } else {
        setEquipError((res as any)?.message || "Không thể đóng túi");
      }
    } catch (e: any) {
      setEquipError(e?.response?.data?.message || e?.message || "Lỗi khi đóng túi");
    } finally {
      setEquipAssigning(false);
    }
  };

  // "Sẵn sàng giao": túi đã đóng từ trước — xác nhận thủ công, dùng lại luồng changeStatus thường (backend
  // tự lấy kg đã chốt từ trước, không cần gửi lại final_weight/item_count).
  const handleConfirmReadyForDelivery = () => {
    onConfirm(Number(order.id), ORDER_STATUS.READY_DELIVERY);
  };

  // Sub-order guard: đơn có sub-orders không cho chuyển trạng thái
  if (order.has_sub_orders === 1) {
    const subCount = order.sub_orders?.length || 0;
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[450px] max-w-[95vw] p-6 shadow-2xl">
          <h3 className="text-lg font-semibold mb-4 text-center text-amber-600">
            Không thể chuyển trạng thái
          </h3>
          <div className="text-sm text-center text-slate-600 mb-4">
            <p>
              Đơn hàng <b>{order.code ?? `OD-${order.id}`}</b> đã được chia thành{" "}
              <b>{subCount}</b> đơn con.
            </p>
            <p className="mt-2">
              Vui lòng thao tác trên từng đơn con.
            </p>
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 border rounded">
              Đóng
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use helper to get next status (supports both simple and extended workflow)
  const transition = getNextStatusTransition(order);
  if (!transition) return null;

  // Bước cuối xưởng → SẴN SÀNG GIAO: cần nhập số ký (chốt final_weight) + số lượng.
  const isReadyDelivery = transition.nextStatus === ORDER_STATUS.READY_DELIVERY;
  const readyWeightNum = parseFloat(readyWeight);
  const readyWeightValid = !isNaN(readyWeightNum) && readyWeightNum > 0;

  // === Helpers cho form phân loại ===
  const totalItems = categories.reduce((sum, cat) => {
    const qty = parseInt(quantities[cat] || "0", 10);
    return sum + (isNaN(qty) ? 0 : qty);
  }, 0);

  const getClassificationPayload = () => {
    const items = categories
      .map(cat => ({
        category: cat,
        quantity: parseInt(quantities[cat] || "0", 10),
      }))
      .filter(item => item.quantity > 0);

    return {
      items,
      total_items: totalItems,
      note: classNote || undefined,
    };
  };

  const handleFillFromServices = async () => {
    if (!order?.id || fillingFromServices) return;
    setFillingFromServices(true);
    setFillMessage(null);
    try {
      const res = await fetchClassificationFromServices(Number(order.id));
      const items = res?.data?.items || [];
      const skipped = res?.data?.skipped || [];
      if (res?.status !== 200) {
        setFillMessage({ type: "warning", text: (res as any)?.message || "Không lấy được số lượng từ dịch vụ" });
        return;
      }
      if (items.length === 0) {
        setFillMessage({
          type: "warning",
          text: skipped.length
            ? `Không suy được loại đồ nào: ${skipped.length} dịch vụ chưa khai loại đồ liên kết hoặc liên kết nhiều loại.`
            : "Đơn không có dịch vụ tính cái/lần để lấy số lượng.",
        });
        return;
      }
      const byLower = new Map(items.map(it => [it.category.trim().toLowerCase(), it]));
      // Tên loại đồ theo danh mục hiện có (giữ đúng cách viết), loại chưa có thì thêm vào cuối.
      const nameFor = (it: { category: string }) =>
        categories.find(c => c.trim().toLowerCase() === it.category.trim().toLowerCase()) ?? it.category;
      setCategories(prev => {
        const lower = new Set(prev.map(c => c.trim().toLowerCase()));
        return [...prev, ...items.filter(it => !lower.has(it.category.trim().toLowerCase())).map(it => it.category)];
      });
      setQuantities(prev => {
        const next = { ...prev };
        for (const it of items) next[nameFor(it)] = String(Number(it.quantity) || 0);
        return next;
      });
      setHiddenCategories(prev => {
        const next = new Set(prev);
        for (const name of prev) if (byLower.has(name.trim().toLowerCase())) next.delete(name);
        return next;
      });
      const skippedText = skipped.length
        ? ` Bỏ qua: ${skipped
            .slice(0, 3)
            .map(s => `${s.item_name} (${s.reason === "no_linked_category" ? "chưa khai loại đồ" : "liên kết nhiều loại"})`)
            .join(", ")}${skipped.length > 3 ? `… và ${skipped.length - 3} dịch vụ khác` : ""}.`
        : "";
      setFillMessage({ type: "success", text: `Đã điền ${items.length} loại đồ từ dịch vụ.${skippedText}` });
    } catch (e: any) {
      setFillMessage({ type: "warning", text: e?.message || "Không lấy được số lượng từ dịch vụ" });
    } finally {
      setFillingFromServices(false);
    }
  };

  const handleConfirm = async () => {
    if (isInspecting) {
      // Sọt/máy giặt là TUỲ CHỌN (đa số chi nhánh bỏ qua bước theo dõi thiết bị): không chọn → lưu phân loại
      // rồi đổi trạng thái qua update-status (luồng web không bắt buộc thiết bị ở bước Kiểm tra).
      if (!selectedProductionEquipId) {
        setEquipAssigning(true);
        setEquipError(null);
        try {
          const clsRes = await saveClassification(Number(order.id), getClassificationPayload());
          if (clsRes?.status !== 200) {
            setEquipError((clsRes as any)?.message || "Không thể lưu phân loại");
            return;
          }
        } catch (e: any) {
          setEquipError(e?.response?.data?.message || e?.message || "Lỗi khi lưu phân loại");
          return;
        } finally {
          setEquipAssigning(false);
        }
        if (transition) onConfirm(Number(order.id), transition.nextStatus);
        return;
      }
      setEquipAssigning(true);
      setEquipError(null);
      try {
        // 1) Lưu phân loại TRƯỚC — endpoint /classification chỉ lưu classification_data, yêu cầu đơn
        //    còn ở bước Kiểm tra (INSPECTING). KHÔNG dùng update-status vì bước gắn thiết bị bên dưới
        //    mới là chỗ tự chuyển sang "Đang sản xuất".
        const classificationData = getClassificationPayload();
        const clsRes = await saveClassification(Number(order.id), classificationData);
        if (clsRes?.status !== 200) {
          setEquipError((clsRes as any)?.message || "Không thể lưu phân loại");
          return;
        }
        // 2) Gắn sọt/máy giặt → backend TỰ chuyển INSPECTING → WASHING (EQUIPMENT_AUTO_TRANSITION).
        //    skip_actor_assignment: KHÔNG tự gán người bấm (quản lý) làm NV giặt — vai giặt theo bảng phân công.
        const res = await assignEquip(selectedProductionEquipId, { order_id: Number(order.id), skip_actor_assignment: true });
        if (res?.status !== 200) {
          setEquipError((res as any)?.message || "Không thể gắn thiết bị");
          return;
        }
      } catch (e: any) {
        setEquipError(e?.response?.data?.message || e?.message || "Lỗi khi hoàn tất kiểm tra");
        return;
      } finally {
        setEquipAssigning(false);
      }
      // Không gọi onConfirm(update-status) nữa — trạng thái đã đổi ở bước gắn thiết bị.
      onEquipmentDone("Đã hoàn thành kiểm tra — đơn chuyển sang Đang sản xuất");
    } else if (isReadyDelivery) {
      const qty = parseInt(readyQty || "0", 10);
      onConfirm(Number(order.id), transition.nextStatus, undefined, {
        final_weight: readyWeightNum,
        item_count: isNaN(qty) ? undefined : qty,
      });
    } else {
      onConfirm(Number(order.id), transition.nextStatus);
    }
  };

  // === INSPECTING: render form phân loại ===
  if (isInspecting) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[550px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
          <h3 className="text-lg font-semibold mb-1 text-center text-cyan-600">
            Kiểm tra & Phân loại
          </h3>
          <p className="text-xs text-center text-slate-500 mb-4">
            Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
          </p>

          {/* Info */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-cyan-700">
              Nhập số lượng cho từng loại đồ giặt. Dữ liệu phân loại sẽ được lưu lại và hiển thị trên đơn hàng.
            </p>
          </div>

          {/* Chọn sọt/máy giặt — TUỲ CHỌN (chỉ để theo dõi sản xuất), chọn 1 trong 2 (không trộn) */}
          <div className="mb-4">
            <label className="text-sm font-semibold text-slate-700 block mb-1">
              Sọt / Máy giặt <span className="text-xs font-normal text-slate-400">(tuỳ chọn)</span>
            </label>
            <select
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-300 focus:outline-none bg-white disabled:bg-slate-100"
              value={selectedProductionEquipId ?? ""}
              disabled={equipListLoading || productionEquipList.length === 0}
              onChange={e => setSelectedProductionEquipId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">
                {equipListLoading ? "Đang tải..." : productionEquipList.length === 0 ? "Không có thiết bị khả dụng" : "-- Không dùng thiết bị --"}
              </option>
              {productionEquipList.map(e => (
                <option key={e.id} value={e.id}>
                  {EQUIPMENT_TYPE_NAME[e.type]} — {e.code}{e.name ? ` (${e.name})` : ""}
                </option>
              ))}
            </select>
          </div>

          {prefillLabel && (
            <p className="text-xs text-emerald-600 mb-3 -mt-2">✓ {prefillLabel}</p>
          )}
          {equipError && (
            <p className="text-xs text-red-600 mb-3 -mt-2">{equipError}</p>
          )}

          {/* Category inputs */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-700">Phân loại đồ giặt</p>
              {/* Lấy SL loại đồ từ SL dịch vụ tính cái/lần (dòng liên kết đúng 1 loại đồ) — ghi đè đúng các loại đó. */}
              <button
                type="button"
                onClick={handleFillFromServices}
                disabled={fillingFromServices || loadingCategories}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-cyan-500 text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
              >
                {fillingFromServices ? "Đang lấy..." : "⇄ Lấy số lượng từ dịch vụ"}
              </button>
            </div>
            {fillMessage && (
              <p className={`text-xs ${fillMessage.type === "success" ? "text-emerald-600" : "text-amber-600"}`}>
                {fillMessage.text}
              </p>
            )}
            {loadingCategories ? (
              <div className="flex items-center justify-center gap-2 py-4">
                <svg className="animate-spin w-4 h-4 text-cyan-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm text-slate-500">Đang tải danh sách loại đồ...</span>
              </div>
            ) : (
              <>
                {categories.filter(cat => !hiddenCategories.has(cat)).map(cat => {
                  const hasQty = (parseInt(quantities[cat] || "0", 10) || 0) > 0;
                  return (
                    <div key={cat} className="flex items-center justify-between gap-3">
                      <label className="text-sm text-slate-600 flex-1">{cat}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          className="w-20 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-cyan-300 focus:outline-none"
                          placeholder="0"
                          value={quantities[cat] || ""}
                          onChange={e => setQuantities(prev => ({ ...prev, [cat]: e.target.value }))}
                        />
                        <span className="text-xs text-slate-400">cái</span>
                        {/* Ẩn lại loại thêm nhầm — chỉ khi chưa nhập số (tránh mất số đã nhập). */}
                        <button
                          type="button"
                          title={hasQty ? "Xoá số lượng trước khi ẩn" : "Ẩn loại đồ này"}
                          disabled={hasQty}
                          onClick={() => setHiddenCategories(prev => new Set(prev).add(cat))}
                          className="w-6 h-6 grid place-items-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Thêm loại đồ — tìm trong các loại đang ẩn (danh mục đã tải sẵn, tìm ngay trên trình duyệt). */}
                {hiddenCategories.size > 0 && (
                  <div className="pt-1">
                    <DropdownSelectOne<{ name: string }>
                      key={hiddenCategories.size}
                      options={categories.filter(cat => hiddenCategories.has(cat)).map(name => ({ name }))}
                      placeholder={`+ Thêm loại đồ (${hiddenCategories.size} loại khác)…`}
                      getOptionLabel={o => o.name}
                      getOptionKey={o => o.name}
                      isSearchLocal
                      handleFilterChange={() => {}}
                      onChange={o =>
                        setHiddenCategories(prev => {
                          const next = new Set(prev);
                          next.delete(o.name);
                          return next;
                        })
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Total */}
          <div className="flex justify-between items-center bg-slate-50 rounded-lg px-4 py-2 mb-4">
            <span className="text-sm font-medium text-slate-700">Tổng cộng</span>
            <span className="text-lg font-bold text-cyan-600">{totalItems} cái</span>
          </div>

          {/* Note */}
          <div className="mb-4">
            <label className="text-sm font-medium text-slate-700 block mb-1">Ghi chú</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-300 focus:outline-none resize-none"
              rows={2}
              placeholder="Ghi chú thêm (VD: Có 2 áo cần tẩy riêng)"
              value={classNote}
              onChange={e => setClassNote(e.target.value)}
            />
          </div>

          {/* Status transition display */}
          <div className="p-3 bg-blue-50 rounded-lg mb-4">
            <div className="flex items-center justify-center gap-3">
              <span className="px-3 py-1.5 bg-cyan-200 text-cyan-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[currentStatus] || "Không rõ"}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-blue-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
              <span className="px-3 py-1.5 bg-blue-200 text-blue-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[transition.nextStatus] || "Không rõ"}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition" disabled={loading}>
              Trở lại
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || equipAssigning}
              className="px-5 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {(loading || equipAssigning) && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Hoàn thành kiểm tra
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === WASHING ("Đang sản xuất"): 3 bước riêng như mobile — Hoàn thành sản xuất (nhập kg, không đổi
  // trạng thái) → Đóng túi (gắn túi, KHÔNG đổi trạng thái — chi nhánh còn kịp "Điều chuyển" trả về gốc)
  // → Sẵn sàng giao (bấm tay, mới thực sự chuyển READY_DELIVERY + broadcast tài xế) ===
  if (isWashing) {
    if (hasBagAttached) {
      return (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[480px] max-w-[95vw] p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-1 text-center text-blue-600">Sẵn sàng giao</h3>
            <p className="text-xs text-center text-slate-500 mb-4">
              Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
            </p>

            {bagJustAttached && (
              <p className="text-sm text-emerald-600 font-medium mb-3 text-center">✓ Đã đóng túi thành công</p>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-700">
                Đơn đã đóng túi ({Number(order.final_weight)} kg). Nếu cần đổi chi nhánh giao (VD nhà máy trả
                về cửa hàng gốc), dùng nút "Điều chuyển" trước. Bấm "Sẵn sàng giao" khi đơn đã sẵn sàng để tài
                xế nhận.
              </p>
            </div>

            {equipError && <p className="text-xs text-red-600 mb-3">{equipError}</p>}

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition" disabled={loading}>
                Trở lại
              </button>
              <button
                onClick={handleConfirmReadyForDelivery}
                disabled={loading}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Sẵn sàng giao
              </button>
            </div>
          </div>
        </div>
      );
    }
    if (hasFinalWeight) {
      return (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[480px] max-w-[95vw] p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-1 text-center text-teal-600">Đóng túi</h3>
            <p className="text-xs text-center text-slate-500 mb-4">
              Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
            </p>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-teal-700">
                Đã nhập số ký sau sản xuất ({Number(order.final_weight)} kg). Chọn túi để đóng gói — đơn
                <b> chưa</b> chuyển trạng thái ngay, sẽ có bước "Sẵn sàng giao" riêng sau khi đóng túi (đủ thời
                gian tạo phiếu điều chuyển nếu cần).
              </p>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Túi <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none bg-white disabled:bg-slate-100"
                value={selectedBagId ?? ""}
                disabled={equipListLoading || bagEquipList.length === 0}
                onChange={e => setSelectedBagId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">
                  {equipListLoading ? "Đang tải..." : bagEquipList.length === 0 ? "Không có túi khả dụng" : "-- Chọn túi --"}
                </option>
                {bagEquipList.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.code}{e.name ? ` (${e.name})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {equipError && <p className="text-xs text-red-600 mb-3 -mt-2">{equipError}</p>}

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition" disabled={equipAssigning}>
                Trở lại
              </button>
              <button
                onClick={handleAssignBag}
                disabled={equipAssigning || !selectedBagId}
                className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition disabled:opacity-50 flex items-center gap-2"
              >
                {equipAssigning && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Đóng túi
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[480px] max-w-[95vw] p-6 shadow-2xl">
          <h3 className="text-lg font-semibold mb-1 text-center text-indigo-600">Hoàn thành sản xuất</h3>
          <p className="text-xs text-center text-slate-500 mb-4">
            Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-700">
              Nhập <b>số ký sau sản xuất</b> để chốt tiền. Đơn vẫn ở "Đang sản xuất" — sau khi lưu, chọn lại
              thao tác này để <b>Đóng túi</b>, rồi bấm <b>"Sẵn sàng giao"</b> khi đơn thực sự sẵn sàng chuyển đi.
            </p>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-slate-600 flex-1">
                Số ký sau sản xuất <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  autoFocus
                  className="w-28 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                  placeholder="0"
                  value={readyWeight}
                  onChange={e => setReadyWeight(e.target.value)}
                />
                <span className="text-xs text-slate-400">kg</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-slate-600 flex-1">Số lượng món</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  className="w-28 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                  placeholder="0"
                  value={readyQty}
                  onChange={e => setReadyQty(e.target.value)}
                />
                <span className="text-xs text-slate-400">cái</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition" disabled={loading}>
              Trở lại
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !readyWeightValid}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Lưu số ký
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === READY_DELIVERY / FOLDING (legacy): nhập số ký + số lượng (chốt giặt) ===
  if (isReadyDelivery) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[480px] max-w-[95vw] p-6 shadow-2xl">
          <h3 className="text-lg font-semibold mb-1 text-center text-blue-600">
            Hoàn thành xưởng → Sẵn sàng giao
          </h3>
          <p className="text-xs text-center text-slate-500 mb-4">
            Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-700">
              Nhập <b>số ký sau giặt</b> để chốt đơn (tính tiền + chia kg cho nhân viên). Thiếu số ký sẽ
              không thể hoàn tất ở bước giao.
            </p>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-slate-600 flex-1">
                Số ký sau giặt <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  autoFocus
                  className="w-28 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
                  placeholder="0"
                  value={readyWeight}
                  onChange={e => setReadyWeight(e.target.value)}
                />
                <span className="text-xs text-slate-400">kg</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-slate-600 flex-1">Số lượng món</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  className="w-28 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
                  placeholder="0"
                  value={readyQty}
                  onChange={e => setReadyQty(e.target.value)}
                />
                <span className="text-xs text-slate-400">cái</span>
              </div>
            </div>
          </div>

          {/* Status transition display */}
          <div className="p-3 bg-blue-50 rounded-lg mb-4">
            <div className="flex items-center justify-center gap-3">
              <span className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[currentStatus] || "Không rõ"}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-blue-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
              <span className="px-3 py-1.5 bg-blue-200 text-blue-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[transition.nextStatus] || "Không rõ"}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition" disabled={loading}>
              Trở lại
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !readyWeightValid}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {transition.label}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === DEFAULT: chuyển trạng thái bình thường ===
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[450px] max-w-[95vw] p-6 shadow-2xl">
        <h3 className="text-lg font-semibold mb-4 text-center text-blue-600">
          Chuyển trạng thái đơn hàng
        </h3>

        <div className="text-sm space-y-3">
          <p><b>Mã đơn:</b> {order.code ?? `OD-${order.id}`}</p>
          <p><b>Khách hàng:</b> {order.customer?.name ?? "—"}</p>
          <p><b>Tổng tiền:</b> {Number(order.total_amount).toLocaleString("vi-VN")} ₫</p>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center gap-3">
              <span className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[currentStatus] || "Không rõ"}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-blue-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
              <span className="px-3 py-1.5 bg-blue-200 text-blue-700 rounded-full text-xs font-semibold">
                {ORDER_STATUS_LABELS[transition.nextStatus] || "Không rõ"}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded" disabled={loading}>
            Trở lại
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Đang xử lý..." : transition.label}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderChangeStatusModal;
